import React, { FC, useContext, useEffect, useState } from 'react'
import RuleTable from './RuleTable'
import { AppContext } from 'Context/AppContext';
import { Link, useHistory } from 'react-router-dom';
import AuthManager from 'Common/AuthManager';
import { toast } from 'react-toastify';
import NoDataComponent from 'Components/Common/NoDataComponent';
import CSSolidLineComponent from '../ComputedSubstitutions/components/CSSolidLineComponent';
import CSConfigHeader from '../ComputedSubstitutions/components/CSConfigHeader';
import { deleteRule, fetchRulesQueue, fetchRuleTableData } from './services/RuleService';
import CSTablePagination from '../ComputedSubstitutions/components/CSTablePagination';
import usePagination from 'Components/Common/hooks/usePagination';
import { RuleContext } from './contexts/RuleContext';
import RulesQueue from 'Components/GMS/Rules/RulesQueue';
import { CSSTransition } from "react-transition-group";
import { debounce } from 'lodash';
import CustomSearchBar from 'Components/GMS/Products/components/CustomSearchBar';
import { ArsContext } from '../ARS/Contexts/ArsContext';
import { updateRecommenderModel } from '../ARS/ARSServices/ARSServices';
import ReloadButton from '../ARS/Components/ReloadButton';
import { RuleSortableColumn, SortType } from './enums';

const RuleView: FC<RuleComponentProps> = ({ mode = 'default', baseCategories, targetCategories }) => {
    const isSidePanel = mode !== 'default';
    const appData = useContext(AppContext);
    const readPermission = appData.pm.checkPermission("gmsrules_read");
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const { newId } = useContext(RuleContext)
    const { totalRecords, handlePageChange, updatePageSize, paginationParams, updateTotalRecords } = usePagination(10)
    const [showRulesQueue, setShowRulesQueue] = useState<boolean>(false);
    const [rulesQueue, setRulesQueue] = useState<RuleQueueItem[]>([]);

    const [data, setData] = useState<Rule[]>([]);
    const [searchString, setSearchString] = useState<string>('');
    const [sortColumn, setSortColumn] = useState<RuleSortableColumn>(RuleSortableColumn.NAME);
    const [sortDirection, setSortDirection] = useState<SortType>(SortType.ASC);
    const { model, setModel, industryId, channelKey, activeSubmodelIndex } = useContext(ArsContext);
    useEffect(() => {
        if (!readPermission) {
            history.push("/workspace/module/permission/denied");
            return;
        }

        if (industryId)
            fetchTableData();
    }, [industryId, channelKey, paginationParams.page, paginationParams.per_page, searchString]);

    useEffect(() => {
        if (paginationParams.page !== 1)
            handlePageChange(1);
        else if (!loading) fetchTableData();
    }, [sortColumn, sortDirection])

    const fetchTableData = async () => {
        setLoading(true);
        let res = await fetchRuleTableData(
            industryId as string,
            channelKey as string,
            paginationParams.page,
            paginationParams.per_page,
            sortColumn,
            sortDirection,
            searchString,
            isSidePanel,
            baseCategories,
            targetCategories
        )
        setData(res?.data?.rules ?? []);
        updateTotalRecords(res?.data?.total ?? 0);
        if (!isSidePanel) fetchRulesQueueData();
        setLoading(false);
    };

    const fetchRulesQueueData = async () => {
        if (!industryId) return;
        let res = await fetchRulesQueue(industryId);
        setRulesQueue(res);
    };

    const handleRulesQueueVisibility = () => {
        setShowRulesQueue((prev) => {
            return !prev;
        });
    };

    const debouncedSearch = debounce((value) => {
        setSearchString(value);
    }, 500);

    const handleDelete = async (rule: Rule) => {
        if (!appData?.pm?.checkPermission("gmsrules_delete")) {
            toast.info("You dont have permission for this operation")
            return
        }
        const permissionStatus = hasPermission(rule, 'delete');
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        await deleteRule(rule?.id);
        fetchTableData()
    }

    const hasPermission = (item: Rule, action: 'delete' | 'edit' | 'create' | 'copy' | 'view' | 'change_owner'): boolean | string => {

        const userobj = AuthManager.getAuthUser();
        const userProfile = userobj?.profile;
        if (!AuthManager.isAuthenticated() || !userProfile) return false;
        if (action === "change_owner") return true
        if (action === 'create' || action === 'copy' || action === 'view') { return true; }

        const userid = userProfile.id;

        if (userid !== item?.created_by_userid) {
            return `You can only ${action} rules that you have created`;
        }

        return true;
    }

    const handleActiveToggle = async (rule: Rule, newValue: boolean) => {
        if (!industryId || !isSidePanel) return;

        const submodel = model?.submodel[activeSubmodelIndex];

        if (submodel) {
            // Determine which rules array to update based on mode
            const rulesKey = mode === 'pure_compatibility' ? 'pure_compatibility_rules' : 'rules';
            const currentRules = submodel[rulesKey] || [];
            const updatedRules = [...currentRules];

            if (newValue) {
                if (!updatedRules.includes(rule.id)) {
                    updatedRules.push(rule.id);
                }
            } else {
                const ruleIndex = updatedRules.indexOf(rule.id);
                if (ruleIndex !== -1) {
                    updatedRules.splice(ruleIndex, 1);
                }
            }

            const updatedSubmodels = model.submodel.map((sm, index) =>
                index === activeSubmodelIndex
                    ? { ...sm, [rulesKey]: updatedRules }
                    : sm
            );

            const updatedModel = {
                ...model,
                submodel: updatedSubmodels
            };

            setModel(updatedModel);

            const hasValidTitles = model?.submodel?.every(x =>
                x.setModePriority.every(mode => mode.title?.trim().length > 0)
            );

            if (!hasValidTitles) {
                toast.error("All mode titles are required. Please ensure each mode has a valid title.");
                return;
            }

            const response = await updateRecommenderModel(updatedModel);

            if (!response?.done) {
                toast.error(response?.message ?? "Could not update the model");
                fetchTableData();
            }
        }
    };

    const handleEditAndCreateVersion = async (rule: Rule, type: 'create' | 'copy' | 'edit' | 'view') => {
        if (!appData?.pm?.checkPermission("gmsrules_create") || !appData?.pm?.checkPermission("gmsrules_update")) {
            toast.info("You dont have permission for this operation")
            return
        }
        const permissionStatus = hasPermission(rule, type);
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        switch (type) {
            case 'edit':
                history.push(`/gms/rule/edit/${rule?.id}`);
                break;
            case 'copy':
                history.push(`/gms/rule/copy/${rule?.id}`);
                break
            case 'view':
                history.push(`/gms/rule/view/${rule?.id}`);
                break
            default:
                history.push(`/gms/rule/edit/0`);
                break;
        }
    };

    const handleClick = async (rule: Rule) => {
        const userobj = AuthManager.getAuthUser();
        const userProfile = userobj?.profile;
        if (!AuthManager.isAuthenticated() || !userProfile) return;
        const isRuleOwner = userProfile?.id === rule?.created_by_userid;
        const permissionStatus = hasPermission(rule, isRuleOwner ? 'edit' : 'view');
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        history.push(`/gms/rule/${isRuleOwner ? 'edit' : 'view'}/${rule?.id}`);
    };

    const showNoPermissionToast = (message?: string) => {
        toast.error(message ?? "You dont have permission for this operation")
    }

    const handleCreate = () => {
        if (!appData?.pm?.checkPermission("gmsrules_create")) {
            toast.info("You dont have permission for this operation")
            return
        }
        history.push("/gms/rule/edit/0")
    }

    const fetchRulesQueueAndSetData = async () => {
        const data = await fetchRulesQueue(industryId as string)
        if (data) setRulesQueue(data)
        else toast.error("Couldnt retrieve Rules Queue data")
    }

    return (
        <>
            <div className="pt-0 tw-w-full tw-mt-0">

                <div className="tw-px-0 tw-overflow-x-hidden">
                    {!isSidePanel && <CSConfigHeader onCLickFunction={handleCreate} linkTo="/gms/rules/list" label="Compatibility" buttonName={"Create"} showBackButton={false} childComponent={
                        <>
                            <div className={"tw-rounded-[4px] tw-flex tw-items-center tw-justify-center tw-gap-[6px]"}>
                                <ReloadButton loading={loading} onClick={() => fetchTableData()} />
                                <Link to={"#"} onClick={handleRulesQueueVisibility} className={`tw-text-sm tw-font-normal tw-text-[#0058DD] tw-w-[180px]`} >
                                    {"Rule Action Queue"}
                                    <span className="tw-ms-2">
                                        <div className="tw-w-6 tw-h-6 tw-px-3 tw-py-0.5 tw-bg-zinc-200 tw-rounded-3xl tw-border tw-justify-center tw-items-center tw-gap-1 tw-inline-flex">
                                            <div className="tw-text-slate-600 tw-text-xs tw-font-normal">
                                                {rulesQueue?.length > 0
                                                    ? rulesQueue?.filter((x) =>
                                                        ["In Progress", "In Queue"].includes(x?.status)
                                                    )?.length
                                                    : 0}
                                            </div>
                                        </div>
                                    </span>
                                </Link>
                            </div>
                            <div className="tw-w-48">
                                <CustomSearchBar
                                    delay={200}
                                    handleSearchChange={(query) => debouncedSearch(query)}
                                    placeholder="Search Rules"
                                    autoComplete="off"
                                />
                            </div>
                        </>
                    } />}
                    <CSSolidLineComponent />
                    <div className="tw-w-[100%] tw-mb-14">
                        <RuleTable
                            ruleList={data}
                            setRuleList={setData}
                            loading={loading}
                            handleDelete={handleDelete}
                            handleEditAndCreateVersion={handleEditAndCreateVersion}
                            fetchTableData={fetchTableData}
                            handleClick={handleClick}
                            NoDataComponent={NoDataComponent}
                            handleActiveToggle={handleActiveToggle}
                            isSidePanel={isSidePanel}
                            newId={newId}
                            sortColumn={sortColumn}
                            setSortColumn={setSortColumn}
                            sortDirection={sortDirection}
                            setSortDirection={setSortDirection}
                            mode={mode}
                        />
                        {!loading && data && data?.length > 0 ?
                            <CSTablePagination
                                paginationParams={paginationParams}
                                totalRecords={totalRecords}
                                updatePageSize={updatePageSize}
                                handlePageChange={handlePageChange} />
                            : null
                        }
                    </div>
                </div>
                {showRulesQueue && (
                    <CSSTransition
                        timeout={1000}
                        key={String(showRulesQueue)}
                        classNames={{
                            enterActive: "animate__animated animate__flipInX",
                            exitActive: "animate__animated animate__flipOutX",
                        }}
                    >
                        <RulesQueue
                            setShowRulesQueue={setShowRulesQueue}
                            rulesQueue={rulesQueue}
                            fetchRulesQueue={fetchRulesQueueAndSetData}
                        />
                    </CSSTransition>
                )}
            </div>
        </>
    )
}

export default RuleView
